import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { ModelsFilterParams, ModelType, ModelsListResponse, CreateModelParams, UpdateModelParams } from '@/types/model'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchModels, fetchModel, updateModel, deleteModel, createModel, bulkDeleteModels } from '@/lib/api'
import { SortEnum } from '@/enums'

// Single model state type
type ModelStateType = {
  isLoading: boolean
  response: ModelType | null
  error: {
    status?: number
    message: string
  } | null
}

// Models list state type
type ModelsListStateType = {
  isLoading: boolean
  response: ModelsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type ModelsStoreState = {
  // Models list filters state
  filters: ModelsFilterParams
  setFilters: (filters: Partial<ModelsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Models list state
  modelsListState: ModelsListStateType
  fetchModels: (params?: ModelsFilterParams) => Promise<ModelsListResponse>
  resetModelsList: () => void

  // Single model state
  modelState: ModelStateType
  getModel: (modelId: string) => Promise<ModelType>
  createModel: (params: CreateModelParams) => Promise<ModelType>
  updateModel: (modelId: string, params: UpdateModelParams) => Promise<ModelType>
  deleteModel: (modelId: string) => Promise<void>
  resetModel: () => void

  // Selection operations
  selectedModelIds: string[]
  selectModel: (id: string) => void
  deselectModel: (id: string) => void
  toggleModelSelection: (id: string) => void
  selectAllModels: (models: ModelType[]) => void
  deselectAllModels: () => void
  isModelSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteModels: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: ModelsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'name'
}

const initialModelState: ModelStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialModelsListState: ModelsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useModelsStore = create<ModelsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    modelState: initialModelState,
    modelsListState: initialModelsListState,
    selectedModelIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof ModelsFilterParams]
          } else {
            updatedFilters[key as keyof ModelsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: ModelsFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as ModelsFilterParams['sortBy']
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value
            break
          default:
            filters[key as keyof ModelsFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    // Single model operations
    getModel: async (modelId: string) => {
      // If already loading, don't make another request
      const currentState = get().modelState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        modelState: {
          ...initialModelState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchModel(modelId)

        set(() => ({
          modelState: {
            ...initialModelState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          modelState: {
            ...initialModelState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createModel: async (params: CreateModelParams) => {
      set(() => ({
        modelState: {
          ...initialModelState,
          isLoading: true
        }
      }))

      try {
        const data = await createModel(params)

        set(() => ({
          modelState: {
            ...initialModelState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          modelState: {
            ...initialModelState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateModel: async (modelId: string, params: UpdateModelParams) => {
      set(() => ({
        modelState: {
          ...get().modelState,
          isLoading: true
        }
      }))

      try {
        const data = await updateModel(modelId, params)

        set(() => ({
          modelState: {
            ...get().modelState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          modelState: {
            ...initialModelState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteModel: async (modelId: string) => {
      set(() => ({
        modelState: {
          ...get().modelState,
          isLoading: true
        }
      }))

      try {
        await deleteModel(modelId)

        set(() => ({
          modelState: initialModelState
        }))
      } catch (error: any) {
        set(() => ({
          modelState: {
            ...initialModelState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetModel: () => {
      set(() => ({
        modelState: initialModelState
      }))
    },

    // Models list operations
    fetchModels: async (params?: ModelsFilterParams) => {
      // If already loading, don't make another request
      const currentState = get().modelsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        modelsListState: {
          ...initialModelsListState,
          isLoading: true
        }
      }))

      try {
        // Use provided params or fall back to store filters
        const filtersToUse = params || get().filters
        const data = await fetchModels(filtersToUse)

        set(() => ({
          modelsListState: {
            ...initialModelsListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          modelsListState: {
            ...initialModelsListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetModelsList: () => {
      set(() => ({
        modelsListState: initialModelsListState
      }))
    },

    // Selection operations
    selectModel: id => {
      set(state => {
        if (state.selectedModelIds.includes(id)) {
          return state
        }
        return { selectedModelIds: [...state.selectedModelIds, id] }
      })
    },

    deselectModel: id => {
      set(state => ({
        selectedModelIds: state.selectedModelIds.filter(modelId => modelId !== id)
      }))
    },

    toggleModelSelection: id => {
      set(state => {
        if (state.selectedModelIds.includes(id)) {
          return { selectedModelIds: state.selectedModelIds.filter(modelId => modelId !== id) }
        }
        return { selectedModelIds: [...state.selectedModelIds, id] }
      })
    },

    selectAllModels: models => {
      set(() => ({
        selectedModelIds: models.map(model => model.id)
      }))
    },

    deselectAllModels: () => {
      set(() => ({ selectedModelIds: [] }))
    },

    isModelSelected: id => {
      return get().selectedModelIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteModels: async ids => {
      set(state => ({
        modelState: {
          ...state.modelState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteModels({ ids })

        set(state => ({
          modelState: {
            ...state.modelState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedModelIds: state.selectedModelIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          modelState: {
            ...state.modelState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useModelsStore
